import { Stack, CircularProgress, Box } from "@mui/material";
import OverviewPage from "./OverviewPage/OverviewPage";
import BasicTeamSectionPage from "./BasicTeamSectionPage";
import PlayerPage from "./PlayerPage";
import { OverviewPageProps } from "./OverviewPage/types";
import { BasicTeamSectionPageProps } from "./BasicTeamSectionPage/types";
import { PlayerReportData } from "./types";
import { mapPlayerDataToPlayerPageProps } from "./utils";
import PrintHeader from "./PrintHeader";
import PrintPageWrapper from "../PrintPageWrapper";
import PlayerBlobPage from "./PlayerBlobPage";
import { TeamReportList } from "../util/Requests";
import type { PlayerStatsPlayerResponse } from "../models/PlayerStats";
import OwnershipPostGamePage from "./OwnershipPostGamePage";
import { TableOfContentsSection } from "./TableOfContents/types";
import TableOfContents from "./TableOfContents";
import ReportPickerMasthead from "./ReportPickerMasthead";
import DevSourceLabel from "../components/Elements/DevSourceLabel";
import { FeatureFlags } from "../util/featureFlags";
import type { GameShotProfile, ReportSourceInfo, TeamReportProps } from "./types";
import PrintTOC, { PrintTOCEntry } from "./PrintTOC";
import GuardLineupsSection from "./GuardLineups/GuardLineupsSection";
import { seasonTypeLabel } from "./utils";

const seasonSpan = (season: number) =>
  `${season}-${String((season + 1) % 100).padStart(2, "0")}`;

export default function TeamReport({
  overviewPageProps,
  halfcourtOffensePageProps,
  halfcourtDefensePageProps,
  transitionOffensePageProps,
  transitionDefensePageProps,
  players,
  playerBlobs,
  availableGameIds,
  reportList,
  selectedReportId,
  onReportChange,
  loading,
  showOwnershipPage,
  teamId,
  season,
  seasonType,
  reportSource,
  gameShotProfile,
  previewStatsSeason,
  previewTeamReport,
  guardLineups,
  hideToc = false,
}: {
  overviewPageProps: OverviewPageProps;
  halfcourtOffensePageProps: BasicTeamSectionPageProps;
  halfcourtDefensePageProps: BasicTeamSectionPageProps;
  transitionOffensePageProps: BasicTeamSectionPageProps;
  transitionDefensePageProps: BasicTeamSectionPageProps;
  players: PlayerReportData[];
  /** Standalone player roll-up blobs keyed by NBA player id — the stitched
   * replacement for the report's retired `players` section (team-only blobs).
   * Fetched by TeamReportv2; rendered for rotation players when `players` is
   * empty. */
  playerBlobs?: Record<string, PlayerStatsPlayerResponse>;
  availableGameIds: string[];
  reportList?: TeamReportList;
  selectedReportId?: string | null;
  onReportChange?: (reportId: string) => void;
  loading?: boolean;
  showOwnershipPage?: boolean;
  /** Team ID for shot detail API calls */
  teamId?: string;
  /** Season for shot detail API calls */
  season?: string;
  /** Season type for detail API calls (e.g., "regular season", "playoff") */
  seasonType?: string;
  /** The blob's provider: labelled in dev builds only */
  reportSource?: ReportSourceInfo;
  /** Game blobs: per-game halfcourt shot profile (ownership page only) */
  gameShotProfile?: GameShotProfile | null;
  /** Roster preview: player pages on this season's numbers */
  previewStatsSeason?: number | null;
  /** Roster preview: that season's full report, whose team pages lead the preview (Trevor, 2026-10-19). Until it
   * loads, or when the season has none, the preview is player pages only. */
  previewTeamReport?: TeamReportProps | null;
  /** POR's guard combinations (LU1): its own page after Rebounding & Transition */
  guardLineups?: TeamReportProps["guardLineups"];
  /** Embedded in another page (the KPI dashboard's packet view): no web contents sidebar. The print contents page
   * stays. */
  hideToc?: boolean;
}) {
  const isSingleGame = !!selectedReportId?.includes("-game-");
  const isPreview = previewStatsSeason != null;
  const playerSeason = isPreview ? String(previewStatsSeason) : season;
  // The team pages read their own report: this one, or on a preview the stats season's. The player pages keep this
  // report's roster and header.
  const teamPages = !isPreview
    ? {
        overviewPageProps,
        halfcourtOffensePageProps,
        halfcourtDefensePageProps,
        transitionOffensePageProps,
        transitionDefensePageProps,
        availableGameIds,
        season,
        seasonType,
        guardLineups,
      }
    : previewTeamReport
      ? {
          ...previewTeamReport,
          season: String(previewStatsSeason),
          seasonType: previewTeamReport.overviewPageProps.headerProps.season_type,
        }
      : null;

  const orderedPlayerIds = overviewPageProps.playerRotations.players.map(
    ({ playerId }) => playerId,
  );
  const playerReportMap = players.reduce((acc, player) => {
    acc.set(player.playerInfo.playerId, player);
    return acc;
  }, new Map<number, PlayerReportData>());
  const orderedPlayers = orderedPlayerIds
    .map((playerId) => playerReportMap.get(playerId))
    .filter((player): player is PlayerReportData => player !== undefined);

  // Team-only reports carry no `players` section: player pages are stitched
  // from the standalone roll-up blobs, ordered by the Rotation section. A
  // rotation player without a blob still gets a (stub) page — never silently
  // dropped.
  // A preseason report (2026-27 · Preseason) has no player pages: the blobs hold no numbers for the season yet, so
  // every page would be an empty stub (Trevor 2026-10-07).
  const preseasonReport =
    !isPreview && !!selectedReportId?.includes("-preseason_season-");
  const blobPlayers =
    players.length === 0 && playerBlobs && !isSingleGame && !preseasonReport
      ? overviewPageProps.playerRotations.players.map((rotation) => ({
          rotation,
          blob: playerBlobs[String(rotation.playerId)],
        }))
      : [];

  // Table of contents
  const playerSections = orderedPlayers.map((player) => ({
    id: `player-${player.playerInfo.playerId}`,
    label: `${player.playerInfo.firstName} ${player.playerInfo.lastName}`,
    isPlayer: true,
  }));
  const blobPlayerSections = blobPlayers.map(({ rotation }) => ({
    id: `player-${rotation.playerId}`,
    label: rotation.playerName,
    isPlayer: true,
  }));

  const tocSections: TableOfContentsSection[] = [
    ...(teamPages
      ? [
          { id: "overview", label: "Overview" },
          { id: "halfcourt-offense", label: teamPages.halfcourtOffensePageProps.title },
          { id: "halfcourt-defense", label: teamPages.halfcourtDefensePageProps.title },
          { id: "reb-transition", label: "Rebounding & Transition" },
          ...(teamPages.guardLineups ? [{ id: "guard-lineups", label: "Guard Lineups" }] : []),
        ]
      : []),
    ...(orderedPlayers.length > 0 || blobPlayers.length > 0
      ? [
          {
            id: "players",
            label: "Players",
          },
          ...playerSections,
          ...blobPlayerSections,
        ]
      : []),
  ];

  // Print TOC (1G): main rows anchor-link to the page ids so print-to-PDF
  // keeps them clickable; sub-items list each page's sections.
  const tocPages = teamPages ?? { overviewPageProps, halfcourtOffensePageProps, halfcourtDefensePageProps,
                                   transitionOffensePageProps, transitionDefensePageProps };
  const header = tocPages.overviewPageProps.headerProps;
  const printTocEntries: PrintTOCEntry[] = [
    {
      anchorId: "overview",
      code: "01",
      title: "Overview",
      subs: [
        "Record & season breakdown",
        "Overall — offense & defense",
        "Game log",
        "Rotation",
      ],
    },
    {
      anchorId: "halfcourt-offense",
      code: "02",
      title: tocPages.halfcourtOffensePageProps.title,
      subs: tocPages.halfcourtOffensePageProps.sectionNames ?? [],
    },
    {
      anchorId: "halfcourt-defense",
      code: "03",
      title: tocPages.halfcourtDefensePageProps.title,
      subs: tocPages.halfcourtDefensePageProps.sectionNames ?? [],
    },
    {
      // Both reb/trans halves live on one condensed page (user call
      // 2026-09-10).
      anchorId: "reb-transition",
      code: "04",
      title: "Rebounding & Transition",
      subs: [
        tocPages.transitionOffensePageProps.title,
        tocPages.transitionDefensePageProps.title,
      ],
    },
    ...(teamPages?.guardLineups
      ? [{ anchorId: "guard-lineups", code: "05", title: "Guard Lineups",
           subs: teamPages.guardLineups.groups.map((g) => g.label) }]
      : []),
  ];
  const printTocPlayers = [
    ...playerSections.map(({ id, label }) => ({ anchorId: id, name: label })),
    ...blobPlayerSections.map(({ id, label }) => ({
      anchorId: id,
      name: label,
    })),
  ];
  const actualStandings = header.conferenceStandingsActual;

  return (
    <Box>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(255, 255, 255, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      <Stack direction="row">
        {!showOwnershipPage && !hideToc && <TableOfContents sections={tocSections} />}
        <Stack
          className="TeamReport"
          sx={{
            flex: 1,
            minWidth: { xs: "fit-content", md: 0 },
            "@media print": { minWidth: 0 },
            backgroundColor: "#fff",
            ".MuiTypography-h4, .MuiTypography-h5": {
              fontWeight: 700,
              textTransform: "uppercase",
            },
          }}
        >
          {showOwnershipPage ? (
            <>
              {reportList && onReportChange && (
                <ReportPickerMasthead
                  reportList={reportList}
                  selectedReportId={selectedReportId}
                  onReportChange={onReportChange}
                  availableGameIds={availableGameIds}
                  loading={loading}
                  windowGameIds={overviewPageProps?.headerProps?.window?.gameIds}
                />
              )}
              <OwnershipPostGamePage
                overviewPageProps={overviewPageProps}
                halfcourtOffensePageProps={halfcourtOffensePageProps}
                halfcourtDefensePageProps={halfcourtDefensePageProps}
                transitionOffensePageProps={transitionOffensePageProps}
                transitionDefensePageProps={transitionDefensePageProps}
                gameShotProfile={gameShotProfile}
                teamId={teamId}
                season={season}
                seasonType={seasonType}
                reportSource={reportSource}
              />
            </>
          ) : (
            <>
              {reportList && onReportChange && (
                <ReportPickerMasthead
                  reportList={reportList}
                  selectedReportId={selectedReportId}
                  onReportChange={onReportChange}
                  availableGameIds={availableGameIds}
                  loading={loading}
                  windowGameIds={overviewPageProps?.headerProps?.window?.gameIds}
                />
              )}
              {FeatureFlags.sourceLabel && reportSource?.source && (
                <Box sx={{ px: 2, pt: 1 }}>
                  <DevSourceLabel source={reportSource.source} />
                </Box>
              )}
              {teamPages && (
                <>
              {!isSingleGame && (
                <PrintTOC
                  teamId={header.teamId}
                  teamName={header.teamName}
                  teamNickname={header.teamNickname}
                  teamAbbrev={header.teamAbbrev}
                  season={header.season}
                  seasonType={header.season_type}
                  gamesPlayed={
                    actualStandings
                      ? actualStandings.wins + actualStandings.losses
                      : undefined
                  }
                  record={
                    actualStandings
                      ? `${actualStandings.wins}-${actualStandings.losses}`
                      : undefined
                  }
                  asOfDate={header.asOfDate}
                  entries={printTocEntries}
                  players={printTocPlayers}
                  playersTitle={
                    isPreview
                      ? `Player Pages · ${seasonSpan(Number(season))} Roster, ${seasonSpan(
                          previewStatsSeason as number,
                        )} Stats`
                      : undefined
                  }
                />
              )}
              {/* No running PrintHeader here — the 1A band carries the
              page's own .ph header strip (user call 2026-08-28). */}
              <PrintPageWrapper id="overview" firstPrinted={isSingleGame}>
                <OverviewPage
                  {...teamPages.overviewPageProps}
                  reportList={reportList}
                  isSingleGame={isSingleGame}
                  availableGameIds={teamPages.availableGameIds}
                  teamId={teamId}
                  season={teamPages.season}
                  seasonType={teamPages.seasonType}
                />
              </PrintPageWrapper>
              <PrintPageWrapper
                id="halfcourt-offense"
                printHeader={
                  <PrintHeader
                    season={header.season}
                    teamId={header.teamId}
                    teamName={header.teamName}
                    asOfDate={header.asOfDate}
                    preliminary={header.feed === "delayed"}
                    rankPopulation={teamPages.overviewPageProps.rankingsProps.hcOffense.rankPopulation}
                    contextLabel="Halfcourt Offense"
                  />
                }
              >
                <BasicTeamSectionPage
                  {...teamPages.halfcourtOffensePageProps}
                  teamId={teamId}
                  season={teamPages.season}
                  seasonType={teamPages.seasonType}
                  isSingleGame={isSingleGame}
                  gameShotProfile={
                    isSingleGame ? gameShotProfile?.offense : undefined
                  }
                />
              </PrintPageWrapper>
              <PrintPageWrapper
                id="halfcourt-defense"
                printHeader={
                  <PrintHeader
                    season={header.season}
                    teamId={header.teamId}
                    teamName={header.teamName}
                    asOfDate={header.asOfDate}
                    preliminary={header.feed === "delayed"}
                    rankPopulation={teamPages.overviewPageProps.rankingsProps.hcOffense.rankPopulation}
                    contextLabel="Halfcourt Defense"
                  />
                }
              >
                <BasicTeamSectionPage
                  {...teamPages.halfcourtDefensePageProps}
                  teamId={teamId}
                  season={teamPages.season}
                  seasonType={teamPages.seasonType}
                  isSingleGame={isSingleGame}
                  gameShotProfile={
                    isSingleGame ? gameShotProfile?.defense : undefined
                  }
                />
              </PrintPageWrapper>
              {/* Both reb/trans halves share one condensed page (2026-09-10) */}
              <PrintPageWrapper
                id="reb-transition"
                printHeader={
                  <PrintHeader
                    season={header.season}
                    teamId={header.teamId}
                    teamName={header.teamName}
                    asOfDate={header.asOfDate}
                    preliminary={header.feed === "delayed"}
                    rankPopulation={teamPages.overviewPageProps.rankingsProps.hcOffense.rankPopulation}
                    contextLabel="Rebounding & Transition"
                  />
                }
              >
                <BasicTeamSectionPage
                  {...teamPages.transitionOffensePageProps}
                  teamId={teamId}
                  season={teamPages.season}
                  seasonType={teamPages.seasonType}
                  isSingleGame={isSingleGame}
                />
                {/* Each reb/trans half gets its own printed page — both on
                one was overflowing into mid-table breaks (2026-09-15) */}
                <Box sx={{ "@media print": { breakBefore: "page" } }}>
                  <BasicTeamSectionPage
                    {...teamPages.transitionDefensePageProps}
                    teamId={teamId}
                    season={teamPages.season}
                    seasonType={teamPages.seasonType}
                    isSingleGame={isSingleGame}
                  />
                </Box>
              </PrintPageWrapper>
              {teamPages.guardLineups && (
                <PrintPageWrapper
                  id="guard-lineups"
                  printHeader={
                    <PrintHeader
                      season={header.season}
                      teamId={header.teamId}
                      teamName={header.teamName}
                      asOfDate={header.asOfDate}
                      preliminary={header.feed === "delayed"}
                      rankPopulation={teamPages.overviewPageProps.rankingsProps.hcOffense.rankPopulation}
                      contextLabel="Guard Lineups"
                    />
                  }
                >
                  <GuardLineupsSection
                    data={teamPages.guardLineups}
                    contextNote={
                      teamPages.season
                        ? `${seasonSpan(Number(teamPages.season))} · ${seasonTypeLabel(teamPages.seasonType)}`
                        : undefined
                    }
                  />
                </PrintPageWrapper>
              )}
                </>
              )}
              {orderedPlayers.map((player, index) => (
                <PrintPageWrapper
                  key={player.playerInfo.playerId}
                  id={index === 0 ? "players" : undefined}
                  printHeader={
                    <PrintHeader
                      season={overviewPageProps.headerProps.season}
                      teamId={overviewPageProps.headerProps.teamId}
                      teamName={overviewPageProps.headerProps.teamName}
                      asOfDate={overviewPageProps.headerProps.asOfDate}
                      preliminary={overviewPageProps.headerProps.feed === "delayed"}
                      rankPopulation={
                        overviewPageProps.rankingsProps.hcOffense.rankPopulation
                      }
                      contextLabel={`${player.playerInfo.firstName} ${player.playerInfo.lastName} · ${index + 1} / ${orderedPlayers.length}`}
                    />
                  }
                >
                  <Box id={`player-${player.playerInfo.playerId}`}>
                    <PlayerPage
                      {...mapPlayerDataToPlayerPageProps(player)}
                      teamId={teamId}
                      teamAbbrev={overviewPageProps.headerProps.teamAbbrev}
                      season={season}
                      seasonType={seasonType}
                    />
                  </Box>
                </PrintPageWrapper>
              ))}
              {blobPlayers.map(({ rotation, blob }, index) => (
                <PrintPageWrapper
                  key={rotation.playerId}
                  id={index === 0 ? "players" : undefined}
                  firstPrinted={isPreview && index === 0 && !teamPages}
                  printHeader={
                    <PrintHeader
                      season={overviewPageProps.headerProps.season}
                      teamId={overviewPageProps.headerProps.teamId}
                      teamName={overviewPageProps.headerProps.teamName}
                      asOfDate={overviewPageProps.headerProps.asOfDate}
                      preliminary={overviewPageProps.headerProps.feed === "delayed"}
                      rankPopulation={
                        overviewPageProps.rankingsProps.hcOffense.rankPopulation
                      }
                      contextLabel={[
                        rotation.playerName,
                        blob?.player?.pos,
                        isPreview ? `${seasonSpan(previewStatsSeason as number)} stats` : undefined,
                        `${index + 1} / ${blobPlayers.length}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  }
                >
                  <Box id={`player-${rotation.playerId}`}>
                    <PlayerBlobPage
                      playerId={rotation.playerId}
                      playerName={rotation.playerName}
                      blob={blob}
                      season={playerSeason}
                    />
                  </Box>
                </PrintPageWrapper>
              ))}
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
